'use client';

import { swissCantonLabel } from '@/lib/countries';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchInteraction } from '@/new_components/searchresults/SearchInteractionContext';
import { companyCountryCode, generateCompanySlug } from '@/lib/company-utils';
import { cn } from '@/lib/utils';
import { useLocale } from '@/hooks/use-locale';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { LocaleLink } from '@/components/locale-link';
import { CompanyCountryFlag } from '@/new_components/shared/CompanyCountryFlag';

export interface CompanyCardData {
  euId: string;
  name: string;
  registerId: string | null;
  registerType: string | null;
  registerCourt: string | null;
  city?: string | null;
  street?: string | null;
  postalCode?: string | null;
  totalRevenue: number | null;
  profit: number | null;
  employees: number | null;
  latestYear: number | null;
  houseNo?: string | null;

  logoSrc?: string | null;
  faviconUrl?: string | null;
  /** First sentence/line of the AI-generated company summary, shown under the
   *  meta line on the mobile search card. Optional — older callers can omit. */
  aiSummary?: string | null;
}

export function CompanyCard({
  company,
  view,
  isHighlighted,
}: {
  company: CompanyCardData;
  view: 'card' | 'list';
  isHighlighted?: boolean;
}) {
  const {
    euId,
    name,
    latestYear,
    logoSrc,
    faviconUrl,
    registerId,
    registerType,
    registerCourt,
    city,
  } = company;
  const cardRef = useRef<HTMLDivElement>(null);
  const { setHoveredEuId, selectedEuId } = useSearchInteraction();
  const { locale } = useLocale();
  const { i18n } = useTranslation();
  const isActive = isHighlighted || selectedEuId === euId;

  useEffect(() => {
    if (isActive && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isActive]);

  const slug = generateCompanySlug(
    {
      name,
      sitz: city,
      registerCourt,
      registerType,
      registerId,
    },
    locale,
  );

  const streetWithNo = [company.street, company.houseNo]
    .filter(Boolean)
    .join(' ');
  const addressLine =
    [streetWithNo, company.postalCode, company.city]
      .filter(Boolean)
      .join(', ') || (companyCountryCode(registerType, euId) === 'CH'
        ? swissCantonLabel(registerCourt, i18n.language)
        : registerCourt);
  const isList = view === 'list';

  return (
    <div
      ref={cardRef}
      onMouseEnter={() => setHoveredEuId(euId)}
      onMouseLeave={() => setHoveredEuId(null)}
      className={cn(
        'rounded-xl transition-all',
        isActive && 'bg-accent/20 ring-1 ring-primary/20',
        view === 'list' && 'w-full',
      )}
    >
      <Card
        className={cn(
          'transition-all hover:border-primary/50 hover:shadow-md',
          isActive && 'border-primary bg-transparent shadow-md',
        )}
      >
        <LocaleLink href={slug} className="block">
          <CardContent
            className={cn(
              'flex gap-3 p-3 sm:gap-4 sm:p-4',
              isList ? 'items-center justify-between' : 'flex-col',
              !isList && 'h-full',
            )}
          >
            <div
              className={cn(
                'flex gap-3 sm:gap-4',
                isList ? 'min-w-0 flex-1 items-center' : 'flex-col',
              )}
            >
              {logoSrc && !isList && (
                <div className="flex h-[50px] w-[60px] sm:h-[70px] sm:w-[90px] items-center justify-center overflow-hidden rounded-md bg-white/95 dark:bg-white text-foreground shrink-0 border border-black/5 dark:border-white/20">
                  <img
                    src={logoSrc}
                    alt={`${name ?? euId} logo`}
                    className="max-h-full max-w-full object-contain p-1 sm:p-2"
                    loading="lazy"
                  />
                </div>
              )}

              {/* List view: logo (or favicon fallback) on the left, a square
                  box spanning both text lines. */}
              {isList && (logoSrc || faviconUrl) && (
                <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white/95 dark:bg-white border border-black/5 dark:border-white/20">
                  <img
                    src={logoSrc ?? faviconUrl ?? undefined}
                    alt={`${name ?? euId} logo`}
                    className="max-h-full max-w-full object-contain p-1"
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}

              <div
                className={cn(
                  'flex flex-col gap-2',
                  isList ? 'min-w-0 flex-1' : 'w-full',
                )}
              >
                <div
                  className={cn(
                    'flex items-center gap-2',
                    isList ? 'min-w-0' : 'flex-wrap',
                  )}
                >
                  {faviconUrl && !isList && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-sm bg-white/95 dark:bg-white border border-black/5 dark:border-white/20 shrink-0">
                      <img
                        src={faviconUrl}
                        alt={`${name} favicon`}
                        className="h-3.5 w-3.5 object-contain"
                        onError={(e) => {
                          // Hide favicon if it fails to load
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </span>
                  )}
                  <span
                    className={cn(
                      'text-sm font-semibold text-foreground group-hover:text-primary transition-colors text-left',
                      isList ? 'truncate' : 'line-clamp-1',
                    )}
                  >
                    {name?.trim()}
                  </span>

                  {latestYear && (
                    <Badge size="sm" variant="outline">
                      Latest report {latestYear}
                    </Badge>
                  )}
                </div>

                <div
                  className={cn(
                    'flex items-center text-xs text-muted-foreground',
                    isList
                      ? 'min-w-0 gap-x-3 whitespace-nowrap'
                      : 'flex-wrap gap-x-3 gap-y-1',
                  )}
                >
                  <span className="flex shrink-0 items-center gap-1">
                    EU ID:{' '}
                    <strong className="text-foreground font-medium">
                      {euId}
                    </strong>
                  </span>
                  {addressLine && (
                    <span
                      className={cn(
                        'flex items-center gap-1',
                        isList && 'min-w-0',
                      )}
                    >
                      <span className="opacity-40">•</span>
                      {/* The flag stands where a map pin would: with the
                          country scope on Global, "Cham" alone could be
                          Bavaria or Zug. */}
                      <CompanyCountryFlag
                        countryCode={companyCountryCode(registerType, euId)}
                      />
                      <strong
                        className={cn(
                          'text-foreground font-medium truncate',
                          isList ? '' : 'max-w-[250px] sm:max-w-none',
                        )}
                      >
                        {addressLine}
                      </strong>
                    </span>
                  )}
                </div>
                {/* First-line AI summary preview (mobile search cards). The
                    paragraph is clamped to a single line so the card stays
                    compact; the full summary is on the company detail page. */}
                {company.aiSummary && (
                  <p className="text-xs text-muted-foreground truncate text-left">
                    {company.aiSummary}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </LocaleLink>
      </Card>
    </div>
  );
}
